import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import {
  ICompetitionWatchlistPair,
  IResponse,
  IWatchedTicker,
  IWatchlistItems,
  IWatchlistPerformers,
  enums,
} from '@kattana-io/common';
import { LOG_OUT, SetAuthPayload, SET_AUTH } from '../actions/auth';
import { actionShowModal } from '../actions/modal';
import { actionCreateNotification } from '../actions/notifications';
import {
  actionActivateWatchlist,
  actionAddSuccessWatchlist,
  actionRemoveSuccessWatchlist,
  actionUpdateSuccessWatchlist,
  actionSetWatchlistWatched,
  actionSetWatchlistGainers,
  actionSetWatchlistLoading,
  actionSetWatchlistTab,
  actionSetWatchlistTickers,
  actionSetWatchlistTVL,
  ACTIVATE,
  ADD,
  REMOVE,
  UPDATE,
  SET_TAB,
  actionSetWatchlistCompetition,
} from '../actions/watchlist';
import { IStore } from '../reducers';
import { ModalTypes } from '../reducers/modal';
import { authTokenSelector } from '../selectors/auth';
import { CexAPI, WatchListAPI } from '../store/createStore';
import { LS_WATCHLIST_TAB_KEY, WatchlistTab } from '../reducers/watchlist';

function* onActivate(): any {
  const loggedIn = yield select((state: IStore) => state.auth.loggedIn);
  if (loggedIn) {
    const watched = yield select((state: IStore) => state.watchlist.watched);
    if (watched.length > 0) {
      yield put(actionSetWatchlistTab(WatchlistTab.Star));
    }
  } else {
    let lastTab = WatchlistTab.TVL;
    if (window.localStorage) {
      if (window.localStorage.getItem(LS_WATCHLIST_TAB_KEY)) {
        lastTab = window.localStorage.getItem(
          LS_WATCHLIST_TAB_KEY
        ) as WatchlistTab;
      }
    }
    yield put(actionSetWatchlistTab(lastTab));
  }
}

function* onAuth(action: any): any {
  try {
    const data = action.payload as SetAuthPayload;
    const token = yield select(authTokenSelector);
    yield put(actionSetWatchlistLoading(true));
    if (data.loggedIn && data.wallet && data.network) {
      const res: IResponse<IWatchlistItems> = yield call(
        WatchListAPI.get,
        data.network,
        data.wallet,
        token
      );

      if (res.success && res.data) {
        const { pairs } = res.data;
        if (Array.isArray(pairs)) {
          const sortedPairs = pairs
            .sort((a, b) => a.order - b.order)
            .map((item, idx) => ({ ...item, order: idx }));
          yield put(actionSetWatchlistWatched(sortedPairs));
        } else {
          yield put(actionSetWatchlistWatched([]));
        }
      } else {
        yield put(actionSetWatchlistWatched([]));
      }
    }
  } catch (error) {
    yield put(
      actionCreateNotification({
        kind: 'warning',
        message: 'Failed to fetch watchlist items...',
      })
    );
    console.error(error);
    yield put(actionSetWatchlistWatched([]));
  } finally {
    yield put(actionSetWatchlistLoading(false));
    yield put(actionActivateWatchlist());
  }
}

function* onSetTab(action: any): any {
  const tab = action.payload as WatchlistTab;
  const tvl = yield select((state: IStore) => state.watchlist.tvl);
  const watched = yield select((state: IStore) => state.watchlist.watched);
  const gainers = yield select((state: IStore) => state.watchlist.gainers);
  const authToken = yield select(authTokenSelector);
  const loggedIn = yield select((state: IStore) => state.auth.loggedIn);
  const wallet = yield select((state: IStore) => state.auth.wallet);

  if (tab === WatchlistTab.Star) {
    yield put(actionSetWatchlistTickers(watched));
  } else if (tab === WatchlistTab.Gainers) {
    if (gainers.length > 0) {
      yield put(actionSetWatchlistTickers(gainers));
    } else {
      yield put(actionSetWatchlistLoading(true));
      try {
        const { pairs } = yield select(
          (state: IStore) => state.registry.gainers
        );
        if (Array.isArray(pairs)) {
          yield put(actionSetWatchlistGainers(pairs));
          yield put(actionSetWatchlistTickers(pairs));
        } else {
          yield put(actionSetWatchlistGainers([]));
          yield put(actionSetWatchlistTickers([]));
        }
      } catch (error) {
        yield put(
          actionCreateNotification({
            kind: 'warning',
            message: 'Failed to fetch Gainers...',
          })
        );
        yield put(actionSetWatchlistGainers([]));
        yield put(actionSetWatchlistTickers([]));
        console.error(error);
      } finally {
        yield put(actionSetWatchlistLoading(false));
      }
    }
  } else if (tab === WatchlistTab.TVL) {
    if (tvl.length > 0) {
      yield put(actionSetWatchlistTickers(tvl));
    } else {
      yield put(actionSetWatchlistLoading(true));
      try {
        const res: IResponse<IWatchlistPerformers> = yield call(
          WatchListAPI.tvl
        );
        if (res.success && res.data) {
          const { pairs } = res.data;
          if (Array.isArray(pairs)) {
            yield put(actionSetWatchlistTVL(pairs));
            yield put(actionSetWatchlistTickers(pairs));
          } else {
            yield put(actionSetWatchlistTVL([]));
            yield put(actionSetWatchlistTickers([]));
          }
        }
      } catch (error) {
        yield put(
          actionCreateNotification({
            kind: 'warning',
            message: 'Failed to fetch TVL...',
          })
        );
        yield put(actionSetWatchlistTVL([]));
        yield put(actionSetWatchlistTickers([]));
        console.error(error);
      } finally {
        yield put(actionSetWatchlistLoading(false));
      }
    }
  } else if (tab === WatchlistTab.Competition && wallet && loggedIn) {
    yield put(actionSetWatchlistLoading(true));
    const res: ICompetitionWatchlistPair[] | null = yield call(
      CexAPI.fetchCompetitionWatchlist,
      wallet,
      authToken
    );
    if (res) {
      if (Array.isArray(res)) {
        yield put(actionSetWatchlistCompetition(res));
      } else {
        yield put(actionSetWatchlistCompetition([]));
      }
    } else {
      yield put(actionSetWatchlistCompetition([]));
    }
    yield put(actionSetWatchlistLoading(false));
  }

  if (window.localStorage) {
    window.localStorage.setItem(LS_WATCHLIST_TAB_KEY, tab);
  }
}

function* onAdd(action: any): any {
  const { item, tag, callback } = action.payload;

  try {
    const token = yield select(authTokenSelector);
    const { loggedIn, wallet, network } = yield select(
      (store: IStore) => store.auth
    );

    if (!loggedIn) {
      // show connect modal
      yield put(actionShowModal(ModalTypes.connect, {}));
      return;
    }

    const payload = { tag };

    const res: IResponse<any> = yield call(
      WatchListAPI.addPair,
      network,
      wallet,
      item.id,
      payload,
      token
    );
    if (res.success) {
      const watchedTicker: IWatchedTicker = {
        ...item,
        tag: tag || enums.Tag.Blue,
        order: 0,
      };
      yield put(actionAddSuccessWatchlist(watchedTicker));
    }
    callback?.();
  } catch (error) {
    callback?.();
    yield put(
      actionCreateNotification({
        kind: 'warning',
        message: 'Failed to add item to watchlist...',
      })
    );
    console.error(error);
  }
}

function* onUpdate(action: any): any {
  try {
    const { id, payload } = action.payload;
    const token = yield select(authTokenSelector);
    const { loggedIn, wallet, network } = yield select(
      (store: IStore) => store.auth
    );

    if (!loggedIn) {
      // show connect modal
      yield put(actionShowModal(ModalTypes.connect, {}));
      return;
    }
    yield put(actionUpdateSuccessWatchlist(id, payload));

    const res: IResponse<any> = yield call(
      WatchListAPI.updatePair,
      network,
      wallet,
      id,
      payload,
      token
    );
    if (res.error) {
      console.error(res.error);
    }
  } catch (error) {
    yield put(
      actionCreateNotification({
        kind: 'warning',
        message: 'Failed to update watchlist item...',
      })
    );
    console.error(error);
  }
}

function* onRemove(action: any): any {
  const { id, callback } = action.payload;

  try {
    const token = yield select(authTokenSelector);
    const { wallet, network } = yield select((store: IStore) => store.auth);

    const res: IResponse<any> = yield call(
      WatchListAPI.remove,
      network,
      wallet,
      id,
      token
    );

    if (res.success) {
      yield put(actionRemoveSuccessWatchlist(id));
    }
    callback?.();
  } catch (error) {
    callback?.();
    yield put(
      actionCreateNotification({
        kind: 'warning',
        message: 'Failed to remove item from watchlist...',
      })
    );
    console.error(error);
  }
}

function* onLogOut(): any {
  try {
    yield put(actionSetWatchlistWatched([]));
    const currentTab = yield select((state: IStore) => state.watchlist.tab);
    if (currentTab === WatchlistTab.Star) {
      yield put(actionSetWatchlistTickers([]));
    }
  } catch (error) {
    console.error(error);
  }
}

export default function* watchlistSaga() {
  yield takeLatest(ACTIVATE, onActivate);
  yield takeLatest(SET_AUTH, onAuth);
  yield takeLatest(LOG_OUT, onLogOut);
  yield takeEvery(ADD, onAdd);
  yield takeEvery(REMOVE, onRemove);
  yield takeEvery(UPDATE, onUpdate);
  yield takeLatest(SET_TAB, onSetTab);
}
